import { Typography } from '@mui/material';
import React, { memo, useMemo } from 'react';

import { DateField } from '~/components/DateField';
import { InvestorsTable } from '~/components/InvestorsTable';
import { TextField } from '~/components/TextField';
import { GetCompanyResponse } from '~/services/rtkQuery/company/types';
import { CreateCompanyFormValues } from '~/types/formValues';
import { calculateInvestorTotalFieldCount } from '~/utils/calculations';

import styles from './styles.module.scss';

interface Props {
  data: GetCompanyResponse['result']['data']['rounds'][number];
}

const Component: React.FC<Props> = ({ data }) => {
  const announcedDate = useMemo(() => {
    return data.summary.announced_date ? new Date(data.summary.announced_date) : null;
  }, [data.summary.announced_date]);

  const dealDate = useMemo(() => {
    return new Date(data.summary.deal_date);
  }, [data.summary.deal_date]);

  const investors = useMemo(() => {
    const uniqueInvestors: CreateCompanyFormValues['form']['rounds'][number]['subrounds'][number]['investors'] =
      [];
    const investors = data.sub_rounds.map(subround => subround.investors).flat();
    investors.forEach(investor => {
      const existingInvestorIndex = uniqueInvestors.findIndex(
        uniqueInvestor => uniqueInvestor.id === investor.external_id,
      );

      if (existingInvestorIndex === -1) {
        uniqueInvestors.push({
          name: investor.name,
          id: investor.external_id,
          lead: investor.lead,
          new: investor.new,
          corporate: investor.corporate,
          pbId: investor.pb_id || null,
        });
      } else {
        if (investor.corporate) {
          uniqueInvestors[existingInvestorIndex].corporate = true;
        }

        if (investor.lead) {
          uniqueInvestors[existingInvestorIndex].lead = true;
        }

        if (investor.new) {
          uniqueInvestors[existingInvestorIndex].new = true;
        }
      }
    });

    return uniqueInvestors;
  }, [data.sub_rounds]);

  return (
    <div className={styles.summary}>
      <div className={styles.summary__fields}>
        <TextField
          disabled
          label="Total Employees"
          value={data.summary.total_employees}
          variant="outlined"
        />
        <TextField
          disabled
          label="Total Investors"
          value={data.summary.total_investors}
          variant="outlined"
        />
        <TextField
          disabled
          label="Total New Investors"
          value={data.summary.total_new_investors}
          variant="outlined"
        />
        <TextField
          disabled
          label="Total Deal Size"
          value={data.summary.total_deal_size}
          variant="outlined"
        />
        <TextField disabled label="Share Sold" value={data.summary.share_sold} variant="outlined" />
        <TextField disabled label="Post Money" value={data.summary.post_money} variant="outlined" />
        <TextField disabled label="Pre Money" value={data.summary.pre_money} variant="outlined" />
        <TextField
          disabled
          label="# of Months from First Financing"
          value={`${data.summary.months_from_first_financing} months`}
          variant="outlined"
        />
        <TextField
          disabled
          label="# of Months between Announcement and Final Close"
          value={`${data.summary.months_between_announcement_and_final_close} months`}
          variant="outlined"
        />
        <TextField
          disabled
          label="# of Months between Closings"
          value={`${data.summary.months_between_closings} months`}
          variant="outlined"
        />
        <DateField
          disabled
          emptyDatePlaceholder="No announcement"
          label="Announced Date*"
          value={announcedDate}
        />
        <DateField disabled label="Deal Date" value={dealDate} />
        <TextField
          disabled
          label="Appreciation between Closings per month"
          value={`${data.summary.appreciation_between_closings_per_month * 100}%`}
          variant="outlined"
        />
        {data.summary.appreciation_per_month && (
          <TextField
            disabled
            label="Appreciation per Month"
            value={`${data.summary.appreciation_per_month * 100}%`}
            variant="outlined"
          />
        )}
        {data.summary.months_since_the_previous_round && (
          <TextField
            disabled
            label="Months since the previous Round"
            value={`${data.summary.months_since_the_previous_round} months`}
            variant="outlined"
          />
        )}
      </div>
      <div>
        <Typography color="text.primary" fontWeight="700" mb="9px" variant="subtitle1">
          Investors{' '}
          <Typography component="span" variant="subtitle1">
            (Total {investors.length})
          </Typography>
        </Typography>
        <InvestorsTable
          data={investors}
          totalCorporate={calculateInvestorTotalFieldCount(investors, 'corporate')}
          totalFollowOn={investors.length - calculateInvestorTotalFieldCount(investors, 'new')}
          totalLeads={calculateInvestorTotalFieldCount(investors, 'lead')}
          totalNew={calculateInvestorTotalFieldCount(investors, 'new')}
        />
      </div>
    </div>
  );
};

export const Summary = memo(Component);
